import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import spIcon from "../../assets/brand/swlogo.png";
import spWordmark from "../../assets/brand/swletters.png";
import api from "../../api/api";
import { toast } from "react-toastify";

export const StoreInformation = () => {
  const [storeName, setStoreName] = useState("");
  const [storePhoneNumber, setStorePhoneNumber] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [logo, setLogo] = useState(null);
  const [banner, setBanner] = useState(null);

  const [logoPreview, setLogoPreview] = useState("");
  const [bannerPreview, setBannerPreview] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const handleFetchStore = async () => {
    try {
      setLoading(true);
      const res = await api.business.getStore();

      if (res?.shop) {
        const shop = res.shop;

        localStorage.setItem("hasStore", "true");

        setStoreName(shop.storeName || "");
        setStorePhoneNumber(shop.phoneNumber || "");
        setLogoPreview(shop.logo || "");
        setBannerPreview(shop.banner || "");

        if (shop.paymentDetails) {
          setBankName(shop.paymentDetails.bankName || "");
          setAccountName(shop.paymentDetails.accountName || "");
          setAccountNumber(shop.paymentDetails.accountNumber || "");
        }
      }
    } catch (error) {
      console.log("Error fetching store:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleFetchStore();
  }, []);

  const isComplete = useMemo(() => {
    return (
      storeName.trim() &&
      storePhoneNumber &&
      bankName.trim() &&
      accountName.trim() &&
      accountNumber.trim()
    );
  }, [storeName, storePhoneNumber, bankName, accountName, accountNumber]);

  const handleNext = async (e) => {
    e.preventDefault();
    if (!isComplete || submitting) return;

    try {
      setSubmitting(true);
      const formData = new FormData();

      formData.append("storeName", storeName);
      formData.append("phoneNumber", storePhoneNumber);
      formData.append("bankName", bankName);
      formData.append("accountName", accountName);
      formData.append("accountNumber", accountNumber);

      if (logo) formData.append("logo", logo);
      if (banner) formData.append("banner", banner);

      await api.business.setup(formData);
      localStorage.setItem("hasStore", "true");
      navigate("/verify");

      navigate("/verify");
    } catch (err) {
      console.warn("Setup shop failed:", err?.status, err?.data || err?.message);
      toast.warn(err?.data?.error || "Failed to set up shop.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    setLogo(file);
    if (file) {
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    setBanner(file);
    if (file) {
      setBannerPreview(URL.createObjectURL(file));
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#fff8e1]">
      <div className="w-[412px] [font-family:'Nunito',Helvetica] text-[#5d4037]">
        
        {/* Logo */}
        <div className="w-full flex justify-center">
          <div className="inline-flex items-center gap-[0px] leading-none">
            <img src={spIcon} alt="Sweet Plate logo" className="shrink-0 select-none object-contain w-[65px] h-[86px] -mt-[2px]" />
            <img src={spWordmark} alt="Sweet Plate" className="shrink-0 select-none object-contain h-[29px] w-auto -ml-[4px]" />
          </div>
        </div>

        <div className="w-full flex items-center mt-4">
          <button
            onClick={() => navigate(-1)}
            className="ml-4 w-9 h-9 rounded-full bg-[#ccb357] grid place-items-center"
            aria-label="Back"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="none"
              stroke="#5d4037"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <h1 className="flex-1 text-center mt-3 ml-6 font-bold text-[22px] leading-7">My Store</h1>
          <div className="w-9" />
        </div>

        {/* Section header */}
        <div className="w-[378px] mx-auto mt-6">
          <p className="font-extrabold uppercase text-[14px] tracking-wide">Store Information</p>
          <p className="mt-1 text-[14px] text-[#5d4037]">
            Provide your store details and payment information to start selling.
          </p>
        </div>

        {/* FORM */}
        <form onSubmit={handleNext} className="mt-4">
          
          {/* Store Name */}
          <div className="w-[378px] mx-auto mt-3">
            <label className="block text-base font-semibold mb-2">Store Name</label>
            <input
              value={storeName}
              onChange={(e) => setStoreName(e.target.value)}
              placeholder="PDP Kitchen"
              className="w-full h-[56px] bg-[#fff8e1] rounded-[18px] border border-[#5d4037] px-[17px] text-[#7a6f5c] text-base outline-none"
              type="text"
            />
          </div>

          {/* Store Phone Number */}
          <div className="w-[378px] mx-auto mt-3">
            <label className="block text-base font-semibold mb-2">Store Phone Number</label>
            <input
              value={storePhoneNumber}
              onChange={(e) => setStorePhoneNumber(e.target.value)}
              placeholder="08012345678"
              className="w-full h-[56px] bg-[#fff8e1] rounded-[18px] border border-[#5d4037] px-[17px] text-[#7a6f5c] text-base outline-none"
              type="text"
            />
          </div>

          {/* Bank Name */}
          <div className="w-[378px] mx-auto mt-5">
            <label className="block text-base font-semibold mb-2">Bank Name</label>
            <input
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder="GTBank"
              className="w-full h-[56px] bg-[#fff8e1] rounded-[18px] border border-[#5d4037] px-[17px] text-[#7a6f5c] text-base outline-none"
              type="text"
            />
          </div>

          {/* Account Name */}
          <div className="w-[378px] mx-auto mt-5">
            <label className="block text-base font-semibold mb-2">Account Name</label>
            <input
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              placeholder="John Doe"
              className="w-full h-[56px] bg-[#fff8e1] rounded-[18px] border border-[#5d4037] px-[17px] text-[#7a6f5c] text-base outline-none"
              type="text"
            />
          </div>

          {/* Account Number */}
          <div className="w-[378px] mx-auto mt-5">
            <label className="block text-base font-semibold mb-2">Account Number</label>
            <input
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              placeholder="0123456789"
              className="w-full h-[56px] bg-[#fff8e1] rounded-[18px] border border-[#5d4037] px-[17px] text-[#7a6f5c] text-base outline-none"
              type="text"
              inputMode="numeric"
            />
          </div>

          {/* Logo Upload */}
          <div className="w-[378px] mx-auto mt-5">
            <label className="block text-base font-semibold mb-2">Store Logo</label>

            {logoPreview && (
              <img
                src={logoPreview}
                alt="Logo preview"
                className="w-24 h-24 rounded-lg object-cover mb-2"
              />
            )}

            <input
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              className="block w-full text-sm text-[#5d4037] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#f7c945] file:text-[#5d4037] hover:file:opacity-90"
            />
          </div>

          {/* Banner Upload */}
          <div className="w-[378px] mx-auto mt-5">
            <label className="block text-base font-semibold mb-2">Store Banner</label>

            {bannerPreview && (
              <img
                src={bannerPreview}
                alt="Banner preview"
                className="w-full h-24 rounded-lg object-cover mb-2"
              />
            )}

            <input
              type="file"
              accept="image/*"
              onChange={handleBannerChange}
              className="block w-full text-sm text-[#5d4037] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#f7c945] file:text-[#5d4037] hover:file:opacity-90"
            />
          </div>

          {/* Submit */}
          <div className="w-[378px] mx-auto pb-16">
            <button
              type="submit"
              disabled={!isComplete}
              className={`mt-7 mx-auto block w-[290px] h-[60px] rounded-[50px] bg-[#f7c945] font-bold text-[#5d4037] text-base leading-[22px] transition-opacity ${
                isComplete
                  ? "hover:opacity-90"
                  : "opacity-50 cursor-not-allowed pointer-events-none"
              }`}
            >
              {submitting ? "Saving..." : "Next"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StoreInformation;
